<template>
    <Teleport to=".button-settings">
        <div class="live-chat-mobile">
            <GameSettingsButtonLiveChatBetMobile
                :betsList="betsList"
                @handleShowCharts="handleShowCharts"
                :isShowCharts="isShowCharts"
            />
            <q-slide-transition appear :duration="100">
                <div class="stats-charts" v-if="isShowCharts">
                    <GameSettingsButtonLiveChatCharts
                        :chartConfig="chartConfig"
                        class="!w-full !h-[100px]"
                        v-if="isShowCharts"
                    />
                </div>
            </q-slide-transition>
            <div class="live-chat-arrow" :style="arrowLeft" />
        </div>
    </Teleport>
</template>
<script setup lang="ts">
// stores
import { useLiveChatStore } from '@/stores/cansino/liveChat';
import { useGameHistoryStore } from '@/stores/cansino/gameHistory';
import { useJoinStateStore } from '@/stores/cansino/joinState';
import useSettingsStore from '~/stores/settings';
// functions
import useTranslation from '~/i18n/useTransLation';
import betHelper from '@/common/js/betHelper';
import { accSub, accAdd } from '@/pages/casino/plinkoGame/js/module';

const isShowCharts = ref(false);
const betsData = ref(useLiveChatStore().bets);
const min = ref(0);
const max = ref(0);
const center = ref(null);
const chartConfig = ref({}); // echarts图表配置
const isIos = ref(false); // 是否ios端

const betsList = ref({
    wagered: '0.00', // 总押注
    profit: '0.00', // 总收益
    win: 0, // 赢局次数
    lose: 0, // 输局次数
});
const arrowLeft = ref('');

const t = useTranslation();
const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    isShow: {
        type: Boolean,
        default: false,
    },
    liveChatIconWidth: {
        type: Number,
        default: 0,
    },
});

const emit = defineEmits(['update:modelValue']);
// 缓存历史下注数据
const historyData = computed(() => {
    // console.log('%cuseGameHistoryStore().gameHistoryDelay', 'background: pink;font-size: 16px;', useGameHistoryStore().gameHistoryDelay.response)
    const response = useGameHistoryStore().gameHistoryDelay.response;
    const aCopied = response.map(cur=>{
        return deepCopy(cur);
    });
    return aCopied;
});

onMounted(() => {
    isShowCharts.value = false;
    isIos.value = /iPad|iPhone|iPod/.test(navigator.userAgent);
});
/**
 * 初始化数据
 */
const initData = async () => {
    useLiveChatStore().bets = [0];
    betsData.value = [0];
    chartConfig.value = {};
    min.value = 0;
    max.value = 0;
    betsList.value = {
        wagered: '0.00',
        profit: '0.00',
        win: 0,
        lose: 0,
    };
};
/**
 * 显示、隐藏charts
 */
const handleShowCharts = () => {
    console.log('handleShowCharts');
    isShowCharts.value = !isShowCharts.value;
};
/**
 * 设置echarts图表配置项
 */
const setChartConfig = () => {
    chartConfig.value = {
        xAxis: {
            show: true,
            type: 'category',
            boundaryGap: false,
            axisLine: {
                lineStyle: {
                    width: 1,
                    color: '#191E28',
                },
            },
        },
        yAxis: {
            type: 'value',
            show: false,
            min: min.value,
            max: max.value,
        },
        grid: {
            left: 0,
            top: '1%',
            right: 0,
            bottom: '1%',
        },
        visualMap: {
            show: false,
            pieces: [
                {
                    gt: betsData.value[0], // gt-大于
                    lte: max.value, // lte-小于等于
                    color: '#1BB83D', // val > betsData.value[0] && val <= max.value
                },
                {
                    gt: min.value,
                    lte: betsData.value[0],
                    color: '#F52E5A', // val > min.value && val <= betsData.value[0]
                },
            ],
        },
        series: [
            {
                data: betsData.value,
                type: 'line',
                smooth: true,
                symbol: 'none',
                lineStyle: {
                    width: 1,
                    markPoint: {
                        symbol: 'none',
                    },
                },
                areaStyle: {
                    opacity: 0.8,
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {
                                offset: 0,
                                color: 'rgba(45, 220, 75, 0.4)',
                            },
                            {
                                offset: center.value,
                                color: 'transparent',
                            },
                            {
                                offset: 1,
                                color: 'rgba(245, 46, 90, 0.4)',
                            },
                        ],
                    },
                },
            },
        ],
    };
};
/**
 * 统计计算收支及设置折线图的展示数据
 * @param currentBetData 当前下注结果
 */
const handleCalculate = (currentBetData) => {
    console.log('currentBetData', currentBetData)
    const { amount, winAmount } = currentBetData;
    // console.log(winAmount, 'winAmount')
    // 下注结果-下注金额=盈利
    const accVal = accSub(winAmount, amount);
    // 计算当前总盈利
    const nextVal = getFixed(accAdd(betsData.value[betsData.value.length - 1], accVal));
    betsData.value.push(Number(nextVal));
    // console.log(betsList.value, 'betsList.value')
    // 总押注
    const wagered = String(getFixed(Number(betsList.value.wagered) + amount));
    betsList.value = {
        wagered,
        profit: String(nextVal),
        win: winAmount && winAmount >= amount ? ++betsList.value.win : betsList.value.win,
        lose: !winAmount || winAmount < amount ? ++betsList.value.lose : betsList.value.lose,
    };
    useLiveChatStore().bets = betsData.value;
    min.value = Math.min(...betsData.value);
    max.value = Math.max(...betsData.value);
    center.value =
        max.value <= 0
            ? 0
            : max.value > 0 && min.value >= 0
            ? 1
            : Math.abs(max.value) / (max.value + Math.abs(min.value));
};
// 监听路由变化
watch(
    () => useJoinStateStore().gameOfRoute,
    (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
            emit('update:modelValue', false);
        }
    },
    {
        deep: true,
    },
);
// 监听币种切换 重置数据状态
watch(
    () => useSettingsStore().pickedCurrency,
    async (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
            await initData();
            emit('update:modelValue', false);
        }
    },
    {
        deep: true,
    },
);
watch(
    () => props.isShow,
    () => {
        // 初始化数据
        initData();
    },
    {
        deep: true,
        immediate: true,
    },
);

// 监听下注数据变化 更新折线图数据展示
watch(
    () => historyData.value,
    async (newVal, oldVal) => {
        if (
            newVal?.length &&
            JSON.stringify(newVal) !== JSON.stringify(oldVal) &&
            newVal?.length >= oldVal?.length
        ) {
            console.log('用于图表的历史数据 ↓');
            console.log(newVal, oldVal);
            // 防止第一次刷新页面后，出现判定错误的情况
            if (oldVal[0]?._id === newVal[0]?._id) {
                return;
            }
            const lastOldValHash = oldVal[0]?.hash;
            const lastIndex = newVal.findIndex((val) => val.hash === lastOldValHash);
            // lastIndex > 1说明这一轮游戏同时下注了多次,需要全部统计计算
            // eg:Circle游戏
            if (lastIndex > 1) {
                // 获取这一轮游戏的下注数据
                const bettedDatas = newVal.filter((val, index) => index < lastIndex);
                bettedDatas.forEach((item) => {
                    handleCalculate(item);
                });
                // lastIndex === 1则证明这一轮游戏只下注了一次
            } else {
                handleCalculate(newVal[0]);
            }
            await Sleep(300);
            setChartConfig();
        }
    },
    {
        deep: true,
    },
);
// 监听livechat按钮宽度，动态设置箭头位置left
watch(
    () => props.liveChatIconWidth,
    (newVal) => {
        if (newVal) {
            arrowLeft.value = `left: ${props.liveChatIconWidth / 2 - 6}px`;
        }
    },
    {
        deep: true,
        immediate: true,
    },
);
</script>
<style scoped lang="scss">
.live-chat-mobile {
    background: linear-gradient(180deg, #003370, transparent);
    width: 100%;
    height: auto;
    position: relative;
    border-radius: 14px;
    padding: 12px;

    .live-chat-arrow {
        height: 12px;
        width: 12px;
        background: linear-gradient(180deg, #003370, transparent);
        position: absolute;
        top: -6px;
        left: max(8.5%, 9vw);
        transform: rotate(45deg);
    }
}
</style>
